import {Injectable} from '@angular/core';

const UserDataKey = 'user';
const SelectedThemeKey = 'selected-theme';
const RealUserDataKey = 'realUser';
import {User} from "../models/user";
import {AclPermission} from "../models/acl/acl-group";
import {BehaviorSubject} from 'rxjs';
import {jwtDecode} from 'jwt-decode';
import {UserData} from '../models/user-data';
import {RestApiService} from './api/rest-api.service';


export function getUserData(): UserData {
  return JSON.parse(localStorage.getItem(UserDataKey) ?? '{}') as UserData;
}

export function setUserData(data: UserData) {
  localStorage.setItem(UserDataKey, JSON.stringify(data));
}

export function getToken() {
  return getUserData().token;
}

export function setToken(token: string) {
  const userData = getUserData();
  userData.token = token;
  setUserData(userData);
}

export function reset() {
  localStorage.clear();
}


enum JWTClaims {
  AclGroups = 'AclGroups'
}

@Injectable({
  providedIn: 'root'
})
export class SessionStorageService {
  _cachedToken?: string = undefined;
  _cachedPermissions?: Set<AclPermission> = undefined;
  currentUser$ = new BehaviorSubject(this.userData);

  constructor(private api: RestApiService) {
  }

  reset() {
    reset();
  }

  getToken() {
    return getToken();
  }

  set userData(data: UserData) {
    setUserData(data);
    this.currentUser$.next(data);
  }

  get userData() {
    return getUserData();
  }

  get isAuthenticated() {
    return !!(getUserData().token) && getUserData().active;
  }

  get permissions(): Set<AclPermission> {
    const token = getToken();
    if (!this._cachedPermissions || this._cachedToken !== token) {
      this._cachedPermissions = new Set();
      this._cachedToken = token;
      if (token) {
        const tokenPayload = jwtDecode<any>(token);
        let permissions: any[] = tokenPayload[JWTClaims.AclGroups];
        if (permissions) {
          if (!Array.isArray(permissions)) {
            permissions = [permissions];
          }
          this._cachedPermissions = new Set(permissions.map(x => Number(x)));
        }
      }
    }

    return this._cachedPermissions;
  }


  storeSelectedTheme(selectedTheme: number) {
    localStorage.setItem(SelectedThemeKey, selectedTheme.toString(10));
  }

  getSelectedTheme(): number | null {
    const themeIdStr = localStorage.getItem(SelectedThemeKey);
    if (themeIdStr != null) {
      return parseInt(themeIdStr, 10);
    }
    return null;
  }

  /**
   * Verifica l'utente collegato presenta almeno uno dei permessi specificati in input.
   * @param aclPermissionTest
   */
  hasPermission(aclPermissionTest: AclPermission | AclPermission[]): boolean {
    if (Array.isArray(aclPermissionTest)) {
      return aclPermissionTest.some(requiredAcl => this.permissions.has(requiredAcl));
    }
    return this.permissions.has(aclPermissionTest);
  }

  /**
   * Copia i dati dell'utente attuale e li sostituisce con quelli di un utente dato
   * @param user
   */
  switchUser(user: UserData) {
    localStorage.setItem(RealUserDataKey, <string>localStorage.getItem(UserDataKey));
    this.userData = user;
    setToken(this.userData.token);
  }

  /**
   * Ripristina i dati dell'utente originale
   */
  restoreRealUser() {
    const originalUserData = localStorage.getItem(RealUserDataKey);
    if (originalUserData) {
      localStorage.setItem(UserDataKey, originalUserData);
      localStorage.removeItem(RealUserDataKey);
    }
  }

  /**
   * Restituisce un valore booleano che indica se sono stati memorizzati i dati
   * dell'utente originariamente autenticato
   */
  canRestoreUser() {
    return !!localStorage.getItem(RealUserDataKey);
  }

  async reloadUserData() {
    if (this.isAuthenticated) {
      const response = await this.api.users.getCurrentUser();
      if (response?.data) {
        // TODO: aggiungere l'aggiornamento del token. Inoltre, per ora, viene eseguito solo all'apertura dell'app...
        // quando viene caricato l'utente, automaticamente, viene aggiornata anche la lingua tramite l'observer
        this.userData = {...this.userData, ...response.data};
      }
    }
  }
}
